import { useState } from 'react';
import { Play, CheckCircle2, Lock, Award, ArrowRight, Book, Shield, Package, MapPin, MessageCircle, X, AlertTriangle, Phone, Camera, Clock } from 'lucide-react';

interface TrainingScreenProps {
  onComplete: () => void;
}

interface TrainingModule {
  id: string;
  title: string;
  duration: string;
  icon: any;
  completed: boolean;
  locked: boolean;
  description: string;
  topics: string[];
}

interface VideoContent {
  title: string;
  sections: {
    time: string;
    title: string;
    content: string;
  }[];
}

export function TrainingScreen({ onComplete }: TrainingScreenProps) {
  const [completedModules, setCompletedModules] = useState<string[]>([]);
  const [currentModule, setCurrentModule] = useState<string | null>(null);
  const [videoProgress, setVideoProgress] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState<{ [key: string]: string }>({});
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizPassed, setQuizPassed] = useState(false);
  const [showModuleContent, setShowModuleContent] = useState(false);

  const moduleContent: { [key: string]: VideoContent } = {
    'getting-started': {
      title: 'Getting Started with GO7',
      sections: [
        {
          time: '0:00',
          title: 'Welcome to GO7',
          content: 'Welcome to the GO7 rider family! You\'re joining Nigeria\'s fastest-growing delivery network with over 10,000 active riders.'
        },
        {
          time: '1:30',
          title: 'The GO7 App Overview',
          content: 'Learn how to navigate the app: Jobs Feed, Earnings Dashboard, Profile Settings, and Support features.'
        },
        {
          time: '3:00',
          title: 'Going Online',
          content: 'How to start your shift: Check your gear, verify your vehicle, and toggle online to start receiving orders.'
        },
        {
          time: '4:15',
          title: 'Understanding Job Cards',
          content: 'Job cards show: pickup/delivery locations, distance, estimated earnings, and package details. Accept jobs within 30 seconds.'
        }
      ]
    },
    'safety': {
      title: 'Safety Guidelines',
      sections: [
        {
          time: '0:00',
          title: 'Road Safety First',
          content: 'Always wear your helmet and GO7 vest. Follow traffic rules. Never use your phone while riding.'
        },
        {
          time: '2:00',
          title: 'Vehicle Maintenance',
          content: 'Daily checks: tire pressure, brakes, lights, and mirrors. Report mechanical issues immediately.'
        },
        {
          time: '4:00',
          title: 'Personal Safety',
          content: 'Stay in well-lit areas. Use the SOS button in emergencies. Never carry weapons or illegal items.'
        },
        {
          time: '5:30',
          title: 'COVID-19 Protocols',
          content: 'Contactless delivery options. Sanitize hands between deliveries. Wear masks in public spaces.'
        },
        {
          time: '7:00',
          title: 'Insurance & Protection',
          content: 'All active riders are covered by GO7 insurance. Report accidents within 24 hours to claims@go7.com.'
        }
      ]
    },
    'pickup-delivery': {
      title: 'Pickup & Delivery Best Practices',
      sections: [
        {
          time: '0:00',
          title: 'Accepting Orders',
          content: 'Review distance, earnings, and pickup time. Accept orders you can complete on time.'
        },
        {
          time: '1:30',
          title: 'Pickup Process',
          content: 'Arrive at pickup location. Check package condition. Take photos if damaged. Verify order ID with merchant.'
        },
        {
          time: '3:45',
          title: 'Package Handling',
          content: 'Handle packages with care. Keep food deliveries upright. Use delivery bag for temperature control.'
        },
        {
          time: '5:30',
          title: 'Customer Communication',
          content: 'Use in-app chat ONLY. Update customers on delays. Be professional and polite. Never share personal contact info.'
        },
        {
          time: '7:15',
          title: 'Delivery Completion',
          content: 'Take photo proof. Get customer confirmation. Mark as delivered in app. Leave note if customer unavailable.'
        },
        {
          time: '8:45',
          title: 'Failed Deliveries',
          content: 'Contact customer 3 times. Wait 5 minutes. Document with photos. Return package to merchant/hub.'
        }
      ]
    },
    'navigation': {
      title: 'Using the Navigation System',
      sections: [
        {
          time: '0:00',
          title: 'In-App Navigation',
          content: 'GO7 provides turn-by-turn navigation. Red routes show optimal paths. Avoid danger zones marked in orange.'
        },
        {
          time: '2:00',
          title: 'External Maps',
          content: 'Tap "Open in Google Maps" for alternative routing. Always return to GO7 app to update status.'
        },
        {
          time: '3:30',
          title: 'GPS Best Practices',
          content: 'Keep GPS enabled. Stay in open areas for better signal. Report location issues to support.'
        },
        {
          time: '5:00',
          title: 'Finding Addresses',
          content: 'Use landmarks if address unclear. Call customer for directions. Take note of gate codes and entry instructions.'
        }
      ]
    },
    'communication': {
      title: 'Customer Communication Policy',
      sections: [
        {
          time: '0:00',
          title: 'In-App Only Policy',
          content: '🚨 CRITICAL: Use ONLY the GO7 app for communication. Never share your personal phone number, WhatsApp, or social media.'
        },
        {
          time: '1:30',
          title: 'What NOT to Share',
          content: 'Never share: Phone numbers, email addresses, social media handles, or personal contact information.'
        },
        {
          time: '3:00',
          title: 'Penalty Structure',
          content: '1st offense: ₦10,000 fine. 2nd offense: ₦25,000 + 7-day suspension. 3rd offense: Account termination.'
        },
        {
          time: '4:30',
          title: 'Professional Communication',
          content: 'Be polite and professional. Use quick replies for common updates. Keep conversations delivery-focused.'
        }
      ]
    },
    'gear-compliance': {
      title: 'Mandatory Gear & Compliance',
      sections: [
        {
          time: '0:00',
          title: 'Required Safety Gear',
          content: 'You MUST wear: GO7 branded vest, DOT/ECE certified helmet, and proper riding gear at all times.'
        },
        {
          time: '1:45',
          title: 'Pre-Shift Verification',
          content: 'Take photos of your vest, helmet, and full gear before going online each day.'
        },
        {
          time: '3:00',
          title: 'Random Compliance Checks',
          content: 'GO7 conducts random photo checks during shifts. Non-compliance = immediate suspension.'
        },
        {
          time: '4:15',
          title: 'Maintaining Your Gear',
          content: 'Keep vest clean and visible. Replace helmet if damaged. Report lost/damaged gear within 24 hours.'
        }
      ]
    }
  };

  const modules: TrainingModule[] = [
    {
      id: 'getting-started',
      title: 'Getting Started with GO7',
      duration: '5 min',
      icon: Book,
      completed: completedModules.includes('getting-started'),
      locked: false,
      description: 'Learn the basics of the GO7 rider app and how to start earning',
      topics: ['App navigation', 'Going online', 'Understanding job cards', 'First delivery']
    },
    {
      id: 'safety',
      title: 'Safety Guidelines',
      duration: '8 min',
      icon: Shield,
      completed: completedModules.includes('safety'),
      locked: !completedModules.includes('getting-started'),
      description: 'Essential safety protocols for riders on the road',
      topics: ['Road safety', 'Vehicle maintenance', 'Personal safety', 'Insurance coverage']
    },
    {
      id: 'pickup-delivery',
      title: 'Pickup & Delivery Best Practices',
      duration: '10 min',
      icon: Package,
      completed: completedModules.includes('pickup-delivery'),
      locked: !completedModules.includes('safety'),
      description: 'Master the complete delivery workflow from pickup to completion',
      topics: ['Package inspection', 'Customer communication', 'Proof of delivery', 'Failed deliveries']
    },
    {
      id: 'navigation',
      title: 'Using the Navigation System',
      duration: '6 min',
      icon: MapPin,
      completed: completedModules.includes('navigation'),
      locked: !completedModules.includes('pickup-delivery'),
      description: 'Navigate efficiently using GO7 maps and external navigation',
      topics: ['In-app navigation', 'GPS best practices', 'Finding addresses', 'Route optimization']
    },
    {
      id: 'communication',
      title: 'Customer Communication Policy',
      duration: '5 min',
      icon: MessageCircle,
      completed: completedModules.includes('communication'),
      locked: !completedModules.includes('navigation'),
      description: '🚨 CRITICAL: Learn strict communication policies and penalties',
      topics: ['In-app only rule', 'Prohibited sharing', 'Penalties', 'Professional etiquette']
    },
    {
      id: 'gear-compliance',
      title: 'Mandatory Gear & Compliance',
      duration: '5 min',
      icon: Shield,
      completed: completedModules.includes('gear-compliance'),
      locked: !completedModules.includes('communication'),
      description: 'Required safety gear and daily compliance verification',
      topics: ['GO7 vest & helmet', 'Pre-shift photos', 'Random checks', 'Gear maintenance']
    }
  ];

  const quizQuestions = [
    {
      id: 'q1',
      question: 'What should you do if a customer is not available for delivery?',
      options: [
        'Leave the package at the door',
        'Contact customer 3 times, wait 5 minutes, document with photos',
        'Return the package immediately without trying to contact',
        'Give it to a neighbor without confirmation',
      ],
      correct: 1,
    },
    {
      id: 'q2',
      question: 'When should you use the SOS/Emergency button?',
      options: [
        'When you need directions to a location',
        'When facing immediate danger or emergency situation',
        'When you want to cancel a delivery',
        'When you need regular customer support',
      ],
      correct: 1,
    },
    {
      id: 'q3',
      question: 'What is required before marking a delivery as complete?',
      options: [
        'Customer signature only',
        'Photo proof of delivery only',
        'Both photo proof and customer confirmation',
        'Just GPS location verification',
      ],
      correct: 2,
    },
    {
      id: 'q4',
      question: 'How should you handle a damaged package during pickup?',
      options: [
        'Accept it and deliver anyway without reporting',
        'Refuse to pick it up without documentation',
        'Document the damage with photos and report immediately',
        'Open the package to check contents inside',
      ],
      correct: 2,
    },
    {
      id: 'q5',
      question: 'What MUST you wear while making deliveries?',
      options: [
        'Any comfortable clothes that you prefer',
        'GO7 branded vest and DOT/ECE certified helmet',
        'Business formal professional attire',
        'It doesn\'t matter what you wear',
      ],
      correct: 1,
    },
    {
      id: 'q6',
      question: 'Can you share your personal phone number with customers?',
      options: [
        'Yes, to make communication easier',
        'Only if they ask nicely',
        'NO - Use ONLY in-app communication or face penalties',
        'Yes, but only for regular customers',
      ],
      correct: 2,
    },
    {
      id: 'q7',
      question: 'What happens if you share personal contact info with customers?',
      options: [
        'Nothing, it\'s allowed',
        '1st offense: ₦10,000 fine, 2nd: ₦25,000 + suspension, 3rd: Termination',
        'Just a verbal warning',
        'Small fine of ₦500',
      ],
      correct: 1,
    },
    {
      id: 'q8',
      question: 'What should you do if your GPS signal is weak?',
      options: [
        'Continue delivery without updating status',
        'Cancel all deliveries for the day',
        'Move to open area for better signal and report issue to support',
        'Turn off GPS to save battery',
      ],
      correct: 2,
    },
  ];

  const handleModuleClick = (moduleId: string) => {
    if (modules.find(m => m.id === moduleId)?.locked) return;
    
    setCurrentModule(moduleId);
    setVideoProgress(0);
    setShowModuleContent(true);
    
    // Simulate video progress
    const interval = setInterval(() => {
      setVideoProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 2;
      });
    }, 100);

    // Auto-complete after 5 seconds
    setTimeout(() => {
      clearInterval(interval);
      setCompletedModules(prev => [...prev, moduleId]);
      setVideoProgress(100);
    }, 5000);
  };

  const handleCloseVideo = () => {
    setCurrentModule(null);
    setShowModuleContent(false);
    setVideoProgress(0);
  };

  const handleQuizSubmit = () => {
    let correct = 0;
    quizQuestions.forEach((q) => {
      if (parseInt(quizAnswers[q.id]) === q.correct) {
        correct++;
      }
    });
    
    const percentage = (correct / quizQuestions.length) * 100;
    if (percentage >= 80) {
      setQuizPassed(true);
    } else {
      alert(`You scored ${Math.round(percentage)}%. You need 80% (at least ${Math.ceil(quizQuestions.length * 0.8)} correct answers) to pass. Please try again.`);
      setQuizAnswers({});
    }
  };

  const allModulesComplete = completedModules.length === modules.length;
  const progressPercentage = (completedModules.length / modules.length) * 100;

  if (quizPassed) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-[#00B277]/10 via-white to-[#E0001B]/10 flex flex-col items-center justify-center p-8">
        <div className="w-32 h-32 rounded-full bg-gradient-to-br from-[#00B277] to-[#00D68F] flex items-center justify-center mb-6 shadow-2xl animate-bounce">
          <Award className="w-16 h-16 text-white" />
        </div>
        
        <h1 className="text-[32px] text-[#111111] mb-2 text-center font-bold">
          Congratulations! 🎉
        </h1>
        
        <p className="text-[16px] text-[#6B6B6B] text-center max-w-md mb-8">
          You've successfully completed your GO7 Rider Training and passed the certification exam. You're now ready to start accepting deliveries and earning!
        </p>

        <div className="w-full max-w-md p-6 bg-white rounded-[16px] border-2 border-[#00B277] shadow-xl mb-6">
          <div className="text-center">
            <div className="inline-block px-4 py-1 bg-[#00B277]/10 rounded-full mb-4">
              <p className="text-[12px] text-[#00B277] font-semibold">✓ CERTIFIED RIDER</p>
            </div>
            <p className="text-[14px] text-[#6B6B6B] mb-1">Certification Number</p>
            <p className="text-[24px] font-bold text-[#111111] font-mono">GO7-CERT-{new Date().getFullYear()}-{String(Math.floor(Math.random() * 10000)).padStart(4, '0')}</p>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex justify-between text-[12px] mb-1">
                <span className="text-[#6B6B6B]">Issued:</span>
                <span className="text-[#111111] font-medium">{new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
              </div>
              <div className="flex justify-between text-[12px]">
                <span className="text-[#6B6B6B]">Valid Until:</span>
                <span className="text-[#111111] font-medium">{new Date(Date.now() + 365*24*60*60*1000).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full max-w-md bg-[#E0001B]/10 border border-[#E0001B]/20 rounded-[12px] p-4 mb-6">
          <p className="text-[12px] text-[#E0001B] font-semibold mb-2">📋 REMEMBER:</p>
          <ul className="text-[11px] text-[#6B6B6B] space-y-1 ml-4 list-disc">
            <li>Always wear your GO7 vest and helmet</li>
            <li>Use ONLY in-app communication with customers</li>
            <li>Never share personal contact information</li>
            <li>Take photo proof for every delivery</li>
          </ul>
        </div>

        <button
          onClick={onComplete}
          className="w-full max-w-md h-14 rounded-[12px] bg-gradient-to-r from-[#E0001B] to-[#FF1A35] text-white hover:shadow-lg transition-all flex items-center justify-center gap-2 font-semibold text-[16px]"
        >
          Start Riding & Earning
          <ArrowRight className="w-6 h-6" />
        </button>
      </div>
    );
  }

  if (showQuiz) {
    return (
      <div className="fixed inset-0 bg-white flex flex-col">
        <div className="bg-gradient-to-r from-[#E0001B] to-[#FF1A35] px-4 py-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
              <Award className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h1 className="text-[22px] text-white font-semibold">Final Certification Exam</h1>
              <p className="text-[13px] text-white/90">
                {Object.keys(quizAnswers).length} of {quizQuestions.length} answered
              </p>
            </div>
          </div>
          
          <div className="bg-white/20 rounded-full h-2">
            <div 
              className="bg-white h-full rounded-full transition-all"
              style={{ width: `${(Object.keys(quizAnswers).length / quizQuestions.length) * 100}%` }}
            />
          </div>
        </div>

        <div className="flex-1 px-4 py-6 overflow-auto">
          <div className="bg-yellow-50 border border-yellow-200 rounded-[12px] p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-[13px] font-semibold text-yellow-900 mb-1">Passing Requirements</p>
                <p className="text-[12px] text-yellow-800">
                  You need to score 80% or higher ({Math.ceil(quizQuestions.length * 0.8)} out of {quizQuestions.length} correct answers) to pass.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {quizQuestions.map((question, qIndex) => (
              <div key={question.id} className="bg-gray-50 rounded-[16px] p-5 border border-gray-200">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-8 h-8 rounded-full bg-[#E0001B] text-white flex items-center justify-center font-semibold text-[14px] flex-shrink-0">
                    {qIndex + 1}
                  </div>
                  <p className="text-[15px] font-medium text-[#111111] flex-1 leading-relaxed pt-1">
                    {question.question}
                  </p>
                </div>
                <div className="space-y-2 ml-11">
                  {question.options.map((option, oIndex) => (
                    <label
                      key={oIndex}
                      className={`flex items-start gap-3 p-4 rounded-[12px] cursor-pointer transition-all border-2 ${
                        quizAnswers[question.id] === String(oIndex)
                          ? 'bg-[#E0001B]/5 border-[#E0001B]'
                          : 'bg-white border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name={question.id}
                        value={oIndex}
                        checked={quizAnswers[question.id] === String(oIndex)}
                        onChange={(e) => setQuizAnswers({
                          ...quizAnswers,
                          [question.id]: e.target.value,
                        })}
                        className="w-5 h-5 text-[#E0001B] mt-0.5 flex-shrink-0"
                      />
                      <span className="text-[14px] text-[#111111] leading-relaxed">{option}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={handleQuizSubmit}
            disabled={Object.keys(quizAnswers).length < quizQuestions.length}
            className={`w-full mt-8 h-14 rounded-[12px] font-semibold text-[16px] transition-all flex items-center justify-center gap-2 ${
              Object.keys(quizAnswers).length < quizQuestions.length
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-[#00B277] text-white hover:bg-[#009963] shadow-lg'
            }`}
          >
            {Object.keys(quizAnswers).length < quizQuestions.length
              ? `Answer All Questions (${quizQuestions.length - Object.keys(quizAnswers).length} remaining)`
              : 'Submit Final Exam'
            }
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  if (currentModule && showModuleContent) {
    const content = moduleContent[currentModule];
    const module = modules.find(m => m.id === currentModule);
    
    return (
      <div className="fixed inset-0 bg-black flex flex-col">
        {/* Video Player */}
        <div className="flex-1 relative flex items-center justify-center">
          <div className="text-center px-8">
            <div className="w-24 h-24 rounded-full bg-white/10 flex items-center justify-center mb-6 mx-auto">
              {videoProgress < 100 ? (
                <Play className="w-12 h-12 text-white animate-pulse" />
              ) : (
                <CheckCircle2 className="w-12 h-12 text-[#00B277]" />
              )}
            </div>
            <h2 className="text-[24px] text-white font-semibold mb-4">{content.title}</h2>
            
            <div className="max-w-2xl mx-auto bg-white/10 backdrop-blur-sm rounded-[12px] p-6 mb-6">
              <div className="space-y-4">
                {content.sections.map((section, index) => (
                  <div 
                    key={index}
                    className={`text-left transition-opacity ${
                      (videoProgress / 100) * content.sections.length > index 
                        ? 'opacity-100' 
                        : 'opacity-30'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Clock className="w-4 h-4 text-white/70" />
                        <span className="text-[12px] text-white/70 font-mono">{section.time}</span>
                      </div>
                      <div className="flex-1">
                        <p className="text-[14px] text-white font-semibold mb-1">{section.title}</p>
                        <p className="text-[13px] text-white/80 leading-relaxed">{section.content}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {videoProgress < 100 && (
              <p className="text-white/70 text-[14px]">Training in progress... {Math.round(videoProgress)}%</p>
            )}
            {videoProgress === 100 && (
              <p className="text-[#00B277] text-[16px] font-semibold">✓ Module Complete!</p>
            )}
          </div>

          {/* Close Button */}
          <button
            onClick={handleCloseVideo}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            disabled={videoProgress < 100}
          >
            <X className="w-6 h-6 text-white" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="bg-black/50 backdrop-blur-sm p-4">
          <div className="max-w-2xl mx-auto">
            <div className="h-2 bg-white/20 rounded-full overflow-hidden mb-2">
              <div 
                className="h-full bg-[#00B277] transition-all duration-200"
                style={{ width: `${videoProgress}%` }}
              />
            </div>
            <div className="flex justify-between text-[12px] text-white/70">
              <span>{module?.duration} video</span>
              <span>{Math.round(videoProgress)}% complete</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-white flex flex-col">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#E0001B]/10 via-[#E0001B]/5 to-transparent px-4 py-8">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#E0001B] to-[#FF1A35] flex items-center justify-center shadow-lg">
            <Book className="w-8 h-8 text-white" />
          </div>
          <div className="flex-1">
            <h1 className="text-[28px] text-[#111111] font-bold mb-1">
              Rider Training
            </h1>
            <p className="text-[14px] text-[#6B6B6B]">
              Complete {modules.length} modules and pass the final exam
            </p>
          </div>
        </div>

        {/* Progress */}
        <div className="bg-white rounded-[12px] p-4 shadow-sm border border-gray-200">
          <div className="flex justify-between text-[14px] mb-2">
            <span className="text-[#6B6B6B] font-medium">Overall Progress</span>
            <span className="text-[#E0001B] font-semibold">{completedModules.length} of {modules.length} complete</span>
          </div>
          <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-[#E0001B] to-[#FF1A35] transition-all duration-500"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
          {allModulesComplete && (
            <p className="text-[12px] text-[#00B277] font-semibold mt-2 flex items-center gap-1">
              <CheckCircle2 className="w-4 h-4" />
              All modules complete! Take the final exam below.
            </p>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 px-4 py-4 overflow-auto">
        <div className="space-y-3 mb-6">
          {modules.map((module, index) => {
            const Icon = module.icon;
            return (
              <button
                key={module.id}
                onClick={() => !module.locked && handleModuleClick(module.id)}
                disabled={module.locked}
                className={`w-full p-5 rounded-[16px] border-2 transition-all text-left ${
                  module.completed
                    ? 'bg-gradient-to-r from-[#00B277]/10 to-[#00D68F]/10 border-[#00B277] shadow-sm'
                    : module.locked
                    ? 'bg-gray-50 border-gray-200 opacity-60 cursor-not-allowed'
                    : 'bg-white border-gray-300 hover:border-[#E0001B] hover:shadow-md'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 ${
                    module.completed 
                      ? 'bg-[#00B277] shadow-lg' 
                      : module.locked 
                      ? 'bg-gray-300' 
                      : 'bg-[#E0001B]/10'
                  }`}>
                    {module.completed ? (
                      <CheckCircle2 className="w-7 h-7 text-white" />
                    ) : module.locked ? (
                      <Lock className="w-7 h-7 text-gray-600" />
                    ) : (
                      <Icon className="w-7 h-7 text-[#E0001B]" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="text-[16px] font-semibold text-[#111111]">{module.title}</h3>
                      {module.id === 'communication' && (
                        <span className="px-2 py-0.5 bg-red-100 text-red-700 text-[10px] font-bold rounded-full flex-shrink-0">
                          CRITICAL
                        </span>
                      )}
                    </div>
                    <p className="text-[13px] text-[#6B6B6B] mb-2 leading-relaxed">{module.description}</p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="flex items-center gap-1 text-[12px] text-[#6B6B6B] bg-gray-100 px-2 py-1 rounded">
                        <Clock className="w-3 h-3" />
                        {module.duration}
                      </div>
                      {!module.locked && !module.completed && (
                        <div className="flex items-center gap-1 text-[12px] text-[#E0001B] font-medium">
                          <Play className="w-3 h-3" />
                          Start Module
                        </div>
                      )}
                      {module.locked && (
                        <span className="text-[11px] text-[#6B6B6B]">Complete previous module to unlock</span>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Quiz Section */}
        <div className={`p-6 rounded-[16px] border-2 transition-all ${
          allModulesComplete
            ? 'bg-gradient-to-br from-[#00B277]/10 to-[#00D68F]/10 border-[#00B277] shadow-lg'
            : 'bg-gray-50 border-gray-200'
        }`}>
          <div className="flex items-start gap-4">
            <div className={`w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 ${
              allModulesComplete ? 'bg-[#00B277]' : 'bg-gray-300'
            }`}>
              <Award className="w-7 h-7 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-[18px] font-semibold mb-1 text-[#111111]">Final Certification Exam</h3>
              <p className="text-[14px] text-[#6B6B6B] mb-4">
                {allModulesComplete 
                  ? `Take the ${quizQuestions.length}-question exam to earn your GO7 certification`
                  : 'Complete all training modules above to unlock the final exam'
                }
              </p>
              <button
                onClick={() => setShowQuiz(true)}
                disabled={!allModulesComplete}
                className={`h-12 px-6 rounded-[12px] font-semibold text-[14px] transition-all flex items-center gap-2 ${
                  allModulesComplete
                    ? 'bg-[#00B277] text-white hover:bg-[#009963] shadow-md'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {allModulesComplete ? 'Start Final Exam' : 'Complete Modules First'}
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        <div className="h-8" />
      </div>
    </div>
  );
}
